import { toast } from 'react-toastify';

// Default attachment size in MB
export const DEFAULT_ATTACHMENT_SIZE_MB = process.env
  .REACT_APP_DEFAULT_ATTACHMENT_SIZE_MB
  ? Number(process.env.REACT_APP_DEFAULT_ATTACHMENT_SIZE_MB)
  : 10;

// Default attachment size in bytes
export const DEFAULT_ATTACHMENT_SIZE_B =
  DEFAULT_ATTACHMENT_SIZE_MB * 1024 * 1024;

// Default chat attachment size in MB
export const DEFAULT_CHAT_ATTACHMENT_SIZE_MB = process.env
  .REACT_APP_DEFAULT_CHAT_ATTACHMENT_SIZE_MB
  ? Number(process.env.REACT_APP_DEFAULT_CHAT_ATTACHMENT_SIZE_MB)
  : 2;

// Default chat attachment size in bytes
export const DEFAULT_CHAT_ATTACHMENT_SIZE_B =
  DEFAULT_CHAT_ATTACHMENT_SIZE_MB * 1024 * 1024;

// helper to map known extensions to MIME types (with category)
export const FILE_EXTENSIONS = {
  // images
  jpeg: { type: 'image/jpeg', extension: 'jpeg', category: 'images' },
  png: { type: 'image/png', extension: 'png', category: 'images' },
  gif: { type: 'image/gif', extension: 'gif', category: 'images' },
  bmp: { type: 'image/bmp', extension: 'bmp', category: 'images' },
  webp: { type: 'image/webp', extension: 'webp', category: 'images' },
  svg: { type: 'image/svg+xml', extension: 'svg', category: 'images' },
  tiff: { type: 'image/tiff', extension: 'tiff', category: 'images' },
  jpg: { type: 'image/jpg', extension: 'jpg', category: 'images' },

  // documents
  pdf: { type: 'application/pdf', extension: 'pdf', category: 'documents' },
  msWord: {
    type: 'application/msword',
    extension: 'msWord',
    category: 'documents',
  },
  msWordOpenXml: {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extension: 'msWordOpenXml',
    category: 'documents',
  },
  msExcel: {
    type: 'application/vnd.ms-excel',
    extension: 'msExcel',
    category: 'documents',
  },
  msExcelOpenXml: {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'msExcelOpenXml',
    category: 'documents',
  },
  msPowerPoint: {
    type: 'application/vnd.ms-powerpoint',
    extension: 'msPowerPoint',
    category: 'documents',
  },
  msPowerPointOpenXml: {
    type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: 'msPowerPointOpenXml',
    category: 'documents',
  },
  text: { type: 'text/plain', extension: 'text', category: 'documents' },
  csv: { type: 'text/csv', extension: 'csv', category: 'documents' },
  rtf: { type: 'application/rtf', extension: 'rtf', category: 'documents' },
  msg: {
    type: 'application/vnd.ms-outlook',
    extension: 'msg',
    category: 'documents',
  },
  xml: { type: 'application/xml', extension: 'xml', category: 'documents' },
  json: { type: 'application/json', extension: 'json', category: 'documents' },
  html: { type: 'text/html', extension: 'html', category: 'documents' },
  eml: { type: 'message/rfc822', extension: 'eml', category: 'documents' },

  // videos
  mp4: { type: 'video/mp4', extension: 'mp4', category: 'videos' },
  webm: { type: 'video/webm', extension: 'webm', category: 'videos' },
  oggVideo: { type: 'video/ogg', extension: 'ogg', category: 'videos' },
  avi: { type: 'video/x-msvideo', extension: 'avi', category: 'videos' },
  mov: { type: 'video/quicktime', extension: 'mov', category: 'videos' },
  mkv: { type: 'video/x-matroska', extension: 'mkv', category: 'videos' },

  // audio
  mp3: { type: 'audio/mpeg', extension: 'mp3', category: 'audio' },
  wav: { type: 'audio/wav', extension: 'wav', category: 'audio' },
  m4a: { type: 'audio/mp4', extension: 'm4a', category: 'audio' },
  aac: { type: 'audio/aac', extension: 'aac', category: 'audio' },
  oggAudio: { type: 'audio/ogg', extension: 'ogg', category: 'audio' },

  // archives
  zip: { type: 'application/zip', extension: 'zip', category: 'archives' },
  rar: { type: 'application/vnd.rar', extension: 'rar', category: 'archives' },
  tar: { type: 'application/x-tar', extension: 'tar', category: 'archives' },
  gz: { type: 'application/gzip', extension: 'gz', category: 'archives' },
  '7z': {
    type: 'application/x-7z-compressed',
    extension: '7z',
    category: 'archives',
  },
};

export const DEFAULT_ALLOWED_ATTACHMENT_TYPES = [];

export const DEFAULT_VIEW_ALLOWED_ATTACHMENT_TYPES = [
  FILE_EXTENSIONS.pdf,
  FILE_EXTENSIONS.jpeg,
  FILE_EXTENSIONS.png,
  FILE_EXTENSIONS.jpg,
  FILE_EXTENSIONS.msg,
  FILE_EXTENSIONS.eml,
];

/**
 * Check if the file type is allowed
 * @param {File} file
 * @param {string[]} allowedTypes
 * @returns {boolean}
 */
export function checkFileType(file, allowedTypes) {
  if (!file) return false;

  const isAllowed = allowedTypes.includes(file.type);

  if (!isAllowed) {
    const allowedList = allowedTypes.join(', ');
    toast.error(
      `Invalid file type. Only the following types are allowed: ${allowedList}`,
      {
        className: 'toast-error',
      },
    );
  }
  return isAllowed;
}

/**
 * Check if file size is within limit
 * @param {File} file - The file to check
 * @param {number} maxSizeMB - Max size in megabytes
 * @returns {boolean} - True if valid, false if exceeds limit
 */
export function checkFileSize(file, maxSizeMB) {
  if (!file) return false;

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    toast.error(`File size exceeds ${maxSizeMB}MB limit.`, {
      className: 'toast-error',
    });
    return false;
  }

  return true;
}

/**
 * Download a blob as a file
 * @param {*} blob
 * @param {*} fileName
 */
export function downloadBlobFile(blob, fileName) {
  // Create a blob URL
  const url = window.URL.createObjectURL(blob);

  // Create an <a> element
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;

  // Append to body (needed for Firefox)
  document.body.appendChild(a);

  // Trigger click
  a.click();

  // Clean up
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Function parse filename_20250529063722113.png to extract the file name_timestamp.ext
// return the file name and extension
export const parseFileName = filePath => {
  const parts = filePath.split('_');
  const lastPart = parts.pop();
  const fileName = parts.join('_');
  const timeStampExt = lastPart.split('.');
  return `${fileName}.${timeStampExt?.[1]}`;
};

// Split file path into directory and file name
export function splitFilePath(fullPath) {
  if (typeof fullPath !== 'string') return { filePath: '', fileName: '' };

  const normalizedPath = fullPath.replace(/\\/g, '/'); // Support Windows paths
  const lastSlashIndex = normalizedPath.lastIndexOf('/');

  if (lastSlashIndex === -1) {
    return {
      filePath: '',
      fileName: normalizedPath,
    };
  }

  return {
    filePath: normalizedPath.slice(0, lastSlashIndex),
    fileName: normalizedPath.slice(lastSlashIndex + 1),
  };
}

/**
 * Get file date time stamp
 */
export const getFileDateTimeStamp = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}${month}${day}${hours}${minutes}${seconds}`;
};
